import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { groupBy, first, map, flattenDeep } from 'lodash-es';
import { getUSPhoneNumberValidator, usPhoneNumberError } from 'validators';
import { convertStrToRichText } from 'seagull';
import { getProposalExpiryRange, isProposalExpiryValid } from '~/business-logic/expiryDateLogic';
import { validateAgreementRecipient, validateContact } from '~/business-logic/validators';
import { isUrlValid } from '~/components/AgreementStudioPage/AgreementStudioModal/IntroVideo/IntroVideoModal/IntroVIdeoModal.utils';
import { addCsvLineNumbers, createErrorObject, getEmptyStringError, isBoolField, isBoolTrue, parseCsv, validateCsvColumns, validateCsvRow, invalidEmailError, normalizeSpaces } from '~/components/ImportWizard/ImportWizard.utils';
import { daysOfWeek, netTermsOptions } from '~/constants';
import { legalTermsModel, DraftStatusType } from '~/models';
import { makeAutoApprovalMethodNoticePeriod, makeManualApprovalMethodNoticePeriod } from '~/models/approval-method-notice-period';
import { NewContactModel } from '~/models/ContactModel';
import { makeExpirationDate } from '~/models/expiration-date';
import { makeIntroVideoLink } from '~/models/intro-video/makers';
import { RequestAccountantAccess } from '~/models/request-accountant-access';
import { isValidNewOngoingBilling } from '~/models/service/newOngoingBilling';
import { makeBillUpfrontState } from '~/models/service-billing';
import { extractBundles } from '~/pages/ImportServices/bundlesValidation.utils';
import { getServicesCsvColumnConfig, runBundleValidation } from '~/pages/ImportServices/parseAndValidateServicesCsv';
import { agreementBundleColumnsToValidate, isRowOfBundle, makeImportServicesErrorMessages } from '../ImportServices/servicesValidation.utils';
import { configureValidation, createServiceBundleFromCsvRow, createServiceFromCsvRow, dayOfMonthOptions, getAutoApprovalValidation, getDraftReviewers, getIsPaused, getIsRecurring, getSkipBillingOnAcceptance, makeAgreementsErrorMessages, parseEffectiveDate, parsePriceIncreaseStartsOn, validateAgreementsRecipients, validatePackages } from './validateAgreements.utils';
const defaultCoverLetter = "Dear customer,\nWe've recently started using Anchor Automatic Billing as our billing service provider.\nThis allows us and you to save time and effort, as all invoices are generated automatically.\nSigning this digital agreement will allow your regular payments to happen automatically, and any changes to the agreement or to the payments will require your approval.\n";
const minAutoPriceIncreaseRate = 0.01;
const maxAutoPriceIncreaseRate = 100;
const INTRO_VIDEO_COLUMN = 'intro video';
const ACCOUNTANT_ACCESS_COLUMN = 'accountant access';
const getCsvColumnConfig = (param)=>{
    let { customPagesEnabled = false, killAccountantAccessEnabled = false, serviceValidationTexts, importAgreementsTexts, agreementsErrorMessages, importServicesErrorMessages } = param;
    const config = _object_spread_props(_object_spread({}, getServicesCsvColumnConfig({}, serviceValidationTexts, importServicesErrorMessages)), {
        ['email']: _object_spread_props(_object_spread({
            inputName: 'email',
            required: true,
            manipulateValue: normalizeSpaces,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, invalidEmailError)
        }, configureValidation(validateContact.email)), {
            shouldMatchSameAgreementServices: true
        }),
        ['first name']: _object_spread_props(_object_spread({
            inputName: 'firstName',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, getEmptyStringError(headerName))
        }, configureValidation(validateContact.firstName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['last name']: _object_spread_props(_object_spread({
            inputName: 'lastName',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, getEmptyStringError(headerName))
        }, configureValidation(validateContact.lastName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['company name']: _object_spread_props(_object_spread({
            inputName: 'customerName',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, getEmptyStringError(headerName))
        }, configureValidation(validateContact.companyName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['agreement title']: {
            inputName: 'agreementTitle',
            shouldMatchSameAgreementServices: true
        },
        ['payment terms']: {
            inputName: 'paymentTerms',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.paymentTermsError),
            validate: (paymentTerms)=>{
                const isUponReceipt = (paymentTerms === null || paymentTerms === void 0 ? void 0 : paymentTerms.toLowerCase()) === 'upon receipt';
                const containsAnExpectedNumber = netTermsOptions.includes(parseInt(paymentTerms));
                return isUponReceipt || containsAnExpectedNumber;
            },
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.paymentTermsError),
            shouldMatchSameAgreementServices: true
        },
        ['require payment method']: {
            inputName: 'requirePaymentMethod',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            validate: isBoolField,
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            shouldMatchSameAgreementServices: true
        },
        ['email body']: {
            inputName: 'emailBody',
            shouldMatchSameAgreementServices: true
        },
        ['phone number']: {
            inputName: 'buyerPhone',
            validate: (buyerPhone)=>!buyerPhone || getUSPhoneNumberValidator()('+1' + buyerPhone) === true,
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, usPhoneNumberError),
            shouldMatchSameAgreementServices: true
        },
        ['billed upfront']: {
            inputName: 'billedUpfront',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            validate: isBoolField,
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            shouldMatchSameAgreementServices: false
        },
        ['billing day']: {
            inputName: 'billingDay',
            required: false,
            validate: (billingDay, param)=>{
                let { rowObject } = param;
                const billingOccurrence = (isRowOfBundle(rowObject) ? rowObject.bundleBillingOccurrence : rowObject.billingOccurrence).toLowerCase();
                const isRecurringBilling = [
                    'monthly',
                    'weekly',
                    'biweekly'
                ].includes(billingOccurrence);
                const isValidOneTimeBilling = !isRecurringBilling && billingDay === '';
                const isLast = billingDay.toLowerCase() === 'last';
                const containsAnExpectedNumber = dayOfMonthOptions().includes(parseInt(billingDay));
                const isDayOfWeek = daysOfWeek.map((day)=>day.toLowerCase()).includes(billingDay.toLowerCase());
                const isValidMonthly = billingOccurrence === 'monthly' && (isLast || containsAnExpectedNumber);
                const isValidWeekly = [
                    'weekly',
                    'biweekly'
                ].includes(billingOccurrence) && isDayOfWeek;
                return {
                    valid: isValidMonthly || isValidWeekly || isValidOneTimeBilling,
                    errorType: rowObject.bundleBillingOccurrence ? 'bundles' : 'services'
                };
            },
            validateError: (headerName, rowNumber, errorType)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.billingDayError(errorType || '')),
            shouldMatchSameAgreementServices: false
        },
        ['effective date']: {
            inputName: 'effectiveDate',
            validate: (effectiveDate, param)=>{
                let { now = new Date() } = param;
                return parseEffectiveDate(effectiveDate, now, agreementsErrorMessages).toValidateResult();
            },
            validateError: (headerName, rowNumber, errorType)=>createErrorObject(headerName, rowNumber, errorType || ''),
            shouldMatchSameAgreementServices: true
        },
        ['accountant access']: {
            inputName: 'accountantAccess',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            validate: (accountantAccess)=>isBoolField(accountantAccess),
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            shouldMatchSameAgreementServices: true
        },
        ['mark this service as optional']: {
            inputName: 'optionalService',
            required: false,
            validate: (optionalService)=>{
                return !optionalService || [
                    'included',
                    'not included'
                ].includes(optionalService.toLowerCase());
            },
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.optionalServiceError),
            shouldMatchSameAgreementServices: false
        },
        ['terms and conditions']: {
            inputName: 'termsAndConditions',
            required: false,
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.termsAndConditionsError),
            shouldMatchSameAgreementServices: true
        },
        ['issue invoice on acceptance']: {
            inputName: 'issueInvoiceOnAcceptance',
            validate: (issueInvoiceOnAcceptance)=>isBoolField(issueInvoiceOnAcceptance),
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName))
        },
        ['pause automatic billing']: {
            inputName: 'startPaused',
            validate: (startPaused)=>isBoolField(startPaused),
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName))
        },
        ['agreement note']: {
            inputName: 'extraClause',
            shouldMatchSameAgreementServices: true
        },
        ['amendments auto approval']: getAutoApprovalValidation('amendmentsAutoApproval', agreementsErrorMessages),
        ['packages']: {
            inputName: 'packages',
            required: false
        },
        ['intro video']: {
            inputName: 'introVideoUrl',
            required: false,
            shouldMatchSameAgreementServices: true,
            validate: (introVideoUrl)=>{
                if (introVideoUrl === '') {
                    return true;
                }
                return isUrlValid(introVideoUrl);
            },
            validateError: (headerName, rowNumber)=>{
                return createErrorObject(headerName, rowNumber, agreementsErrorMessages.introVideoUrlError);
            }
        },
        ['automatic yearly price increase rate']: {
            inputName: 'priceIncreaseRate',
            validate (priceIncreaseRate) {
                if (!priceIncreaseRate) {
                    return true;
                }
                const asNumber = +priceIncreaseRate;
                return !isNaN(asNumber) && asNumber >= minAutoPriceIncreaseRate && asNumber <= maxAutoPriceIncreaseRate;
            },
            validateError (headerName, rowNumber) {
                return createErrorObject(headerName, rowNumber, "Invalid price increase rate. Please enter a number between ".concat(minAutoPriceIncreaseRate, " and ").concat(maxAutoPriceIncreaseRate));
            }
        },
        ['automatic yearly price increase starts on']: {
            inputName: 'priceIncreaseStartsOn',
            validate: (priceIncreaseStartsOn, param)=>{
                let { now = new Date(), rowObject: { effectiveDate } } = param;
                return parsePriceIncreaseStartsOn(priceIncreaseStartsOn, effectiveDate, now, agreementsErrorMessages).toValidateResult();
            },
            validateError: (headerName, rowNumber, errorType)=>createErrorObject(headerName, rowNumber, errorType || '')
        },
        ['out of scope charges auto approval']: getAutoApprovalValidation('invoicesAutoApproval', agreementsErrorMessages),
        ['recipient 1 first name']: _object_spread_props(_object_spread({
            inputName: 'recipient1FirstName',
            required: false
        }, configureValidation(validateAgreementRecipient.firstName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 1 last name']: _object_spread_props(_object_spread({
            inputName: 'recipient1LastName',
            required: false
        }, configureValidation(validateAgreementRecipient.lastName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 1 email']: _object_spread_props(_object_spread({
            inputName: 'recipient1Email',
            required: false,
            manipulateValue: normalizeSpaces
        }, configureValidation(validateAgreementRecipient.email)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 1 required to sign']: {
            inputName: 'recipient1RequiredToSign',
            required: false,
            validate: (requiredToSign)=>!requiredToSign || isBoolField(requiredToSign),
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            shouldMatchSameAgreementServices: true
        },
        ['recipient 2 first name']: _object_spread_props(_object_spread({
            inputName: 'recipient2FirstName',
            required: false
        }, configureValidation(validateAgreementRecipient.firstName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 2 last name']: _object_spread_props(_object_spread({
            inputName: 'recipient2LastName',
            required: false
        }, configureValidation(validateAgreementRecipient.lastName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 2 email']: _object_spread_props(_object_spread({
            inputName: 'recipient2Email',
            required: false,
            manipulateValue: normalizeSpaces
        }, configureValidation(validateAgreementRecipient.email)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 2 required to sign']: {
            inputName: 'recipient2RequiredToSign',
            required: false,
            validate: (requiredToSign)=>!requiredToSign || isBoolField(requiredToSign),
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            shouldMatchSameAgreementServices: true
        },
        ['recipient 3 first name']: _object_spread_props(_object_spread({
            inputName: 'recipient3FirstName',
            required: false
        }, configureValidation(validateAgreementRecipient.firstName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 3 last name']: _object_spread_props(_object_spread({
            inputName: 'recipient3LastName',
            required: false
        }, configureValidation(validateAgreementRecipient.lastName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 3 email']: _object_spread_props(_object_spread({
            inputName: 'recipient3Email',
            required: false,
            manipulateValue: normalizeSpaces
        }, configureValidation(validateAgreementRecipient.email)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 3 required to sign']: {
            inputName: 'recipient3RequiredToSign',
            required: false,
            validate: (requiredToSign)=>!requiredToSign || isBoolField(requiredToSign),
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            shouldMatchSameAgreementServices: true
        },
        ['recipient 4 first name']: _object_spread_props(_object_spread({
            inputName: 'recipient4FirstName',
            required: false
        }, configureValidation(validateAgreementRecipient.firstName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 4 last name']: _object_spread_props(_object_spread({
            inputName: 'recipient4LastName',
            required: false
        }, configureValidation(validateAgreementRecipient.lastName)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 4 email']: _object_spread_props(_object_spread({
            inputName: 'recipient4Email',
            required: false,
            manipulateValue: normalizeSpaces
        }, configureValidation(validateAgreementRecipient.email)), {
            shouldMatchSameAgreementServices: true
        }),
        ['recipient 4 required to sign']: {
            inputName: 'recipient4RequiredToSign',
            required: false,
            validate: (requiredToSign)=>!requiredToSign || isBoolField(requiredToSign),
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, agreementsErrorMessages.getInvalidBooleanError(headerName)),
            shouldMatchSameAgreementServices: true
        },
        ['expiration period in days']: {
            inputName: 'proposalExpiryDate',
            required: false,
            shouldMatchSameAgreementServices: true,
            validate (value) {
                const days = +value;
                const isValidProposalExpiryPeriod = !isNaN(days) && Number.isInteger(days) && isProposalExpiryValid(days);
                return value === '' || isValidProposalExpiryPeriod;
            },
            validateError: (headerName, rowNumber)=>{
                const autoProposalExpiryRange = getProposalExpiryRange();
                return createErrorObject(headerName, rowNumber, importAgreementsTexts.invalidProposalExpirationPeriod({
                    minDays: autoProposalExpiryRange.minDays,
                    maxDays: autoProposalExpiryRange.maxDays
                }));
            }
        }
    });
    if (customPagesEnabled) {
        delete config[INTRO_VIDEO_COLUMN];
    }
    if (killAccountantAccessEnabled) {
        delete config[ACCOUNTANT_ACCESS_COLUMN];
    }
    return config;
};
function runValidation(param) {
    let { parsedCsv, availableLegalTerms, now, csvColumnConfig, importAgreementsTexts, agreementsErrorMessages, importServicesErrorMessages } = param;
    const errorContext = {
        phase: 'validation'
    };
    if (!parsedCsv.length) throw new Error('No data');
    validateCsvColumns(parsedCsv, csvColumnConfig);
    const withLineNumbers = addCsvLineNumbers(parsedCsv);
    const groupedServicesByAgreement = groupBy(withLineNumbers, (param)=>{
        let { email, customerName } = param;
        return "".concat(email, ":").concat(customerName);
    });
    const errors = map(groupedServicesByAgreement, (serviceRows)=>{
        const packagesError = validatePackages(serviceRows, agreementsErrorMessages);
        if (packagesError) {
            return packagesError;
        }
        const groupedServicesByAgreementPackage = groupBy(serviceRows, (param)=>{
            let { packages } = param;
            return packages;
        });
        for (const packageName of Object.keys(groupedServicesByAgreementPackage)){
            const { bundleRows: bundles } = extractBundles(groupedServicesByAgreementPackage[packageName]);
            const bundleErrors = runBundleValidation(bundles, agreementBundleColumnsToValidate, importServicesErrorMessages);
            if (bundleErrors.length > 0) {
                return bundleErrors;
            }
        }
        return serviceRows.map((rowObject)=>{
            errorContext['service'] = rowObject.serviceName;
            errorContext['lineNumber'] = rowObject.lineNumber;
            try {
                const recipientsError = validateAgreementsRecipients(rowObject, agreementsErrorMessages);
                if (recipientsError) {
                    return recipientsError;
                }
                return validateCsvRow(rowObject, csvColumnConfig, (columnConfig, columnName)=>{
                    const { inputName, shouldMatchSameAgreementServices } = columnConfig;
                    const { lineNumber, billedUpfront, automaticBilling, issueInvoiceOnAcceptance, effectiveDate, priceIncreaseRate } = rowObject;
                    const value = rowObject[inputName];
                    const isRecurring = getIsRecurring(rowObject);
                    const isAutomaticBilling = isBoolTrue(automaticBilling);
                    const isOngoing = isRecurring && isAutomaticBilling;
                    const isBilledUpfront = isBoolTrue(billedUpfront);
                    const isPaused = getIsPaused(rowObject);
                    const isValidSkipBillingOnAcceptance = isBoolField(issueInvoiceOnAcceptance || '');
                    const skipBillingOnAcceptance = getSkipBillingOnAcceptance(rowObject);
                    if (shouldMatchSameAgreementServices && value !== serviceRows[0][inputName]) {
                        return createErrorObject(columnName, lineNumber, agreementsErrorMessages.sameAgreementUnmatchingDataError);
                    }
                    if (inputName === 'termsAndConditions' && typeof value === 'string' && value !== '' && !availableLegalTerms.some((doc)=>legalTermsModel.name(doc).toLowerCase() === value.toLowerCase() || legalTermsModel.name(doc).toLowerCase() === value.toLowerCase() + '.pdf')) {
                        return createErrorObject(columnName, lineNumber, agreementsErrorMessages.termsAndConditionsError);
                    }
                    if (inputName === 'billingDay' && isBilledUpfront && typeof value === 'string' && value.toLowerCase() === 'last') {
                        return createErrorObject(columnName, lineNumber, agreementsErrorMessages.billUpfrontLastDayError);
                    }
                    if (inputName === 'issueInvoiceOnAcceptance' && isValidSkipBillingOnAcceptance && isOngoing && !isValidNewOngoingBilling({
                        isBilledUpfront,
                        isPaused,
                        isProrated: false,
                        billUpfrontState: makeBillUpfrontState(skipBillingOnAcceptance),
                        startBillingAt: false
                    })) {
                        return createErrorObject(columnName, lineNumber, 'You can issue an invoice on acceptance only for automatic, recurring, unpaused, billed-upfront services');
                    }
                    if ((inputName === 'priceIncreaseRate' || inputName === 'priceIncreaseStartsOn') && !!value && !isRecurring) {
                        const serviceType = isRowOfBundle(rowObject) ? 'bundles' : 'services';
                        return createErrorObject(columnName, lineNumber, agreementsErrorMessages.priceIncreaseOnlyForRecurringError(serviceType));
                    }
                    if (inputName === 'priceIncreaseStartsOn') {
                        if (!value && priceIncreaseRate && effectiveDate) {
                            return createErrorObject(columnName, lineNumber, importAgreementsTexts.priceIncreaseStartsOnRequired());
                        }
                        if (value && !priceIncreaseRate) {
                            return createErrorObject(columnName, lineNumber, 'Automatic yearly price increase rate is required if start date is set');
                        }
                    }
                }, now);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (exn) {
                // eslint-disable-next-line no-console
                console.error({
                    error: '' + exn,
                    errorContext
                });
                if ((exn === null || exn === void 0 ? void 0 : exn.message) != null) {
                    errorContext['exn'] = exn.message;
                    exn.message = JSON.stringify(errorContext);
                }
                throw exn;
            }
        });
    });
    return flattenDeep(errors);
}
const createDraft = (rows, context)=>{
    const { now, availableLegalTerms, generateUniqueId, defaultDraftsValues, defaultAgreementTitle, customPagesEnabled, killAccountantAccessEnabled, agreementsErrorMessages } = context;
    const errorContext = {};
    try {
        const withLineNumbers = addCsvLineNumbers(rows);
        const firstRow = first(withLineNumbers);
        if (firstRow == null) throw new Error('empty CSV');
        const { email, firstName, lastName, buyerPhone, customerName, agreementTitle, paymentTerms, requirePaymentMethod, emailBody, effectiveDate, accountantAccess, termsAndConditions, amendmentsAutoApproval, proposalExpiryDate, extraClause, invoicesAutoApproval } = firstRow;
        errorContext['email'] = email;
        errorContext['customerName'] = customerName;
        errorContext['agreementTitle'] = agreementTitle;
        const isUponReceipt = (paymentTerms === null || paymentTerms === void 0 ? void 0 : paymentTerms.toLowerCase()) === 'upon receipt';
        const netTerms = isUponReceipt ? 0 : parseInt(paymentTerms);
        const allIncludedServicesToCreateAsServices = [];
        const packages = groupBy(withLineNumbers, (param)=>{
            let { packages } = param;
            return packages;
        });
        const packageNames = Object.keys(packages);
        const serviceBundles = packageNames.reduce((acc, packageName)=>{
            const packageRows = packages[packageName];
            const { bundleRows, nonBundleRows } = extractBundles(packageRows);
            const bundles = bundleRows.reduce((acc, b)=>{
                if (!acc[b.bundleName]) {
                    acc[b.bundleName] = [];
                }
                acc[b.bundleName].push(b);
                return acc;
            }, {});
            const bundleServices = [];
            Object.entries(bundles).forEach((param)=>{
                let [bundleName, rows] = param;
                errorContext['phase'] = 'create-service-bundle';
                errorContext['included-service'] = bundleName;
                const { bundleService, includedServicesToCreateAsServices } = createServiceBundleFromCsvRow(rows, context);
                bundleServices.push(bundleService);
                allIncludedServicesToCreateAsServices.push(...includedServicesToCreateAsServices);
            });
            const services = nonBundleRows.map((r)=>{
                errorContext['phase'] = 'create-service';
                errorContext['service'] = r.serviceName;
                return createServiceFromCsvRow(r, context);
            });
            acc.bundles.push({
                id: generateUniqueId('bundle'),
                name: packageName,
                services: [
                    ...bundleServices,
                    ...services
                ]
            });
            return acc;
        }, {
            bundles: []
        });
        errorContext['phase'] = undefined;
        errorContext['service'] = undefined;
        const parsedEffectiveDate = parseEffectiveDate(effectiveDate, now, agreementsErrorMessages);
        let legalTermsDoc = availableLegalTerms.find((doc)=>legalTermsModel.name(doc).toLowerCase() === termsAndConditions.toLowerCase() || legalTermsModel.name(doc).toLowerCase() === termsAndConditions.toLowerCase() + '.pdf');
        if (legalTermsDoc == null && defaultDraftsValues.libraryLegalTermsId) {
            legalTermsDoc = availableLegalTerms.find((doc)=>doc.id === defaultDraftsValues.libraryLegalTermsId);
        }
        return {
            draft: {
                id: generateUniqueId('draft'),
                coverLetter: defaultCoverLetter,
                extraClause: convertStrToRichText(extraClause || ''),
                requirePaymentMethod: isBoolTrue(requirePaymentMethod),
                allowManualPayments: false,
                isExistingCustomer: false,
                customNotificationText: (emailBody === null || emailBody === void 0 ? void 0 : emailBody.length) > 0 ? emailBody : '',
                title: agreementTitle.length > 0 ? agreementTitle : defaultAgreementTitle,
                lastModifiedOn: Date.now(),
                netTerms,
                effectiveDate: parsedEffectiveDate.datestamp(),
                legalTermsDocuments: legalTermsDoc ? [
                    legalTermsModel.toRelationshipLegalTermsDocuments(legalTermsDoc)
                ] : [],
                feeControls: {
                    coverCreditCardFees: false,
                    coverDirectDebitFees: false
                },
                clientContactId: null,
                isSample: false,
                amendmentsApprovalMethod: amendmentsAutoApproval ? makeAutoApprovalMethodNoticePeriod(parseInt(amendmentsAutoApproval)) : makeManualApprovalMethodNoticePeriod(),
                invoicesApprovalMethod: invoicesAutoApproval ? makeAutoApprovalMethodNoticePeriod(parseInt(invoicesAutoApproval)) : makeManualApprovalMethodNoticePeriod(),
                requestAccountantAccess: !killAccountantAccessEnabled && isBoolTrue(accountantAccess) ? RequestAccountantAccess.Mandatory : RequestAccountantAccess.None,
                serviceBundles: packageNames.length > 1 ? serviceBundles : undefined,
                services: packageNames.length > 1 ? [] : serviceBundles.bundles[0].services,
                reviewers: getDraftReviewers(firstRow),
                introVideo: !customPagesEnabled && rows[0].introVideoUrl ? makeIntroVideoLink(rows[0].introVideoUrl) : null,
                expirationDate: makeExpirationDate(Number(proposalExpiryDate)),
                status: {
                    type: DraftStatusType.Active
                }
            },
            contact: NewContactModel.make({
                email: email,
                firstName: firstName,
                lastName: lastName,
                phone: buyerPhone ? '+1' + buyerPhone : null,
                companyName: customerName
            }),
            includedServicesToCreateIfNotExists: allIncludedServicesToCreateAsServices
        };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (exn) {
        // eslint-disable-next-line no-console
        console.error({
            error: '' + exn,
            errorContext
        });
        if ((exn === null || exn === void 0 ? void 0 : exn.message) != null) {
            errorContext['exn'] = exn.message;
            exn.message = JSON.stringify(errorContext);
        }
        throw exn;
    }
};
const createDrafts = (context)=>{
    const { parsedCsv } = context;
    const groupedServicesByAgreement = groupBy(parsedCsv, (param)=>{
        let { email, customerName } = param;
        return "".concat(email, ":").concat(customerName);
    });
    return map(groupedServicesByAgreement, (serviceRows)=>createDraft(serviceRows, context));
};
function tryToParseError(error, or) {
    try {
        return JSON.parse(error);
    } catch (e) {
        return or;
    }
}
export const parseAndValidateAgreementCsv = async (param)=>{
    let { data, availableLegalTerms, now, generateUniqueId, defaultDraftsValues, defaultAgreementTitle, customPagesEnabled = false, killAccountantAccessEnabled = false, t } = param;
    try {
        const validationTexts = t.models.service.validation;
        const importAgreementsTexts = t.pages.importAgreements;
        const importAgreementsValidationTexts = t.pages.importAgreements.validation;
        const importServicesValidationTexts = t.pages.importServices.validation;
        const importServicesErrorMessages = makeImportServicesErrorMessages(importServicesValidationTexts, validationTexts);
        const agreementsErrorMessages = makeAgreementsErrorMessages(importAgreementsValidationTexts);
        const csvColumnConfig = getCsvColumnConfig({
            customPagesEnabled,
            killAccountantAccessEnabled,
            serviceValidationTexts: validationTexts,
            importAgreementsTexts,
            agreementsErrorMessages,
            importServicesErrorMessages
        });
        const ignoredColumns = [
            ...customPagesEnabled ? [
                INTRO_VIDEO_COLUMN
            ] : [],
            ...killAccountantAccessEnabled ? [
                ACCOUNTANT_ACCESS_COLUMN
            ] : []
        ];
        const parsedCsv = await parseCsv(data, csvColumnConfig, ignoredColumns);
        const context = {
            defaultAgreementTitle,
            defaultDraftsValues,
            availableLegalTerms,
            now,
            parsedCsv,
            csvColumnConfig,
            generateUniqueId,
            customPagesEnabled,
            killAccountantAccessEnabled,
            importAgreementsTexts,
            agreementsErrorMessages,
            importServicesErrorMessages
        };
        const errors = runValidation(context);
        if (!errors.length) {
            const items = createDrafts(context);
            return {
                items,
                errors: []
            };
        } else {
            return {
                items: [],
                errors
            };
        }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (exn) {
        var _tryToParseError;
        return {
            items: [],
            errors: [
                {
                    message: '' + exn,
                    rowNumber: ((_tryToParseError = tryToParseError(exn === null || exn === void 0 ? void 0 : exn.message, 0)) === null || _tryToParseError === void 0 ? void 0 : _tryToParseError.lineNumber) || 0
                }
            ]
        };
    }
};
